/**
 * Cart drawer "Arrives" estimate — gldn144 (#600).
 *
 * snippets/gldn-delivery-estimate.liquid renders each method's business-day
 * range as `data-range="<min>-<max>"`; this element turns it into
 * "Nov 12 – Nov 19" from the shopper's own today, counting Monday–Friday only
 * (no holiday calendar, no order cut-off — both out of scope).
 *
 * The drawer re-morphs the summary on every cart write. A morph may replace this
 * element (connectedCallback) or patch it in place, blanking the date text and
 * changing data-range, so a MutationObserver re-fills whenever a span no longer
 * shows the expected text. Writes are skipped when the text already matches,
 * which keeps the observer from feeding itself.
 */

const STORAGE_KEY = 'gldn-delivery-estimate-open';

/**
 * @param {Date} start
 * @param {number} days Business days to add.
 * @returns {Date}
 */
export function addBusinessDays(start, days) {
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
}

/** @param {Date} date @param {'short' | 'long'} month */
function formatDate(date, month = 'short') {
  return date.toLocaleDateString('en-US', { month, day: 'numeric' });
}

/**
 * @param {string} range "<min>-<max>" business days.
 * @param {Date} today
 * @returns {string} "Mmm D – Mmm D", or '' when the range is malformed.
 */
export function formatRange(range, today) {
  return rangeParts(range, today).visible;
}

/**
 * The visible text ("Nov 12 – Nov 19") and what a screen reader should hear
 * instead ("November 12 to November 19") -- an en dash is read as "dash" or
 * skipped, depending on the reader.
 * @param {string} range @param {Date} today
 */
export function rangeParts(range, today) {
  const match = /^(\d+)-(\d+)$/.exec(range || '');
  if (!match) return { visible: '', spoken: '' };
  const start = addBusinessDays(today, Number(match[1]));
  const end = addBusinessDays(today, Number(match[2]));
  return {
    visible: `${formatDate(start)} \u2013 ${formatDate(end)}`,
    spoken: `${formatDate(start, 'long')} to ${formatDate(end, 'long')}`,
  };
}

/* Guarded so the date helpers above can be imported outside a browser (tests). */
if (typeof HTMLElement !== 'undefined' && !customElements.get('gldn-delivery-estimate')) {
  class GldnDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.render();
      this.restoreOpen();
      this.addEventListener('toggle', this.onToggle, true);
      this.observer = new MutationObserver(() => this.render());
      this.observer.observe(this, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-range'],
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('toggle', this.onToggle, true);
    }

    render() {
      const today = new Date();
      for (const el of this.querySelectorAll('[data-range]')) {
        const { visible, spoken } = rangeParts(el.getAttribute('data-range'), today);
        const shown = el.querySelector('[aria-hidden="true"]');
        const said = el.querySelector('.visually-hidden');
        /* Skipped when already current, which keeps the observer from feeding itself. */
        if (shown?.textContent === visible && said?.textContent === spoken) continue;
        el.replaceChildren();
        if (!visible) continue;
        const visibleSpan = document.createElement('span');
        visibleSpan.setAttribute('aria-hidden', 'true');
        visibleSpan.textContent = visible;
        const spokenSpan = document.createElement('span');
        spokenSpan.className = 'visually-hidden';
        spokenSpan.textContent = spoken;
        el.append(visibleSpan, spokenSpan);
      }
    }

    /* The morph rebuilds <details> closed; keep the shopper's choice for the session. */
    restoreOpen() {
      const details = this.querySelector('details');
      try {
        if (details && sessionStorage.getItem(STORAGE_KEY) === '1') details.open = true;
      } catch (_) {
        /* storage blocked: start closed rather than abort before the observer is set */
      }
    }

    onToggle = (event) => {
      if (!(event.target instanceof HTMLDetailsElement)) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, event.target.open ? '1' : '0');
      } catch (_) {
        /* storage blocked: the section still works, it just forgets its state */
      }
    };
  }

  customElements.define('gldn-delivery-estimate', GldnDeliveryEstimate);
}
